// ============================================================
// AUTH & SESSION WRAPPER
// ============================================================
// Auth and the session lifecycle both go through the backend port; nothing
// here knows which backend is behind it. The app is one of five screens -
// the Character Workshop (no login), loading, signing in, the Assets
// library (`?assets`: signed in, before any session), choosing a session -
// and then the simulator, keyed on the session so a new one starts clean. The pieces live in hooks/useShell{Workshop,Auth,SessionEntry}
// and components/shell/{AuthScreen,SessionScreen}.
import { lazy, Suspense, useState } from 'react';
import { useToast } from './contexts/ToastContext.jsx';
import DeathWatchSimulator from './components/DeathWatchSimulator.jsx';
import AuthScreen, { useGmSignInForm } from './components/shell/AuthScreen';
import SessionScreen from './components/shell/SessionScreen';
import { joinCodeFromUrl, useUrlFlag } from './hooks/useUrlFlag';
import useShellAuth from './hooks/useShellAuth';
import useShellSessionEntry from './hooks/useShellSessionEntry';
import MotionAttribute from './components/MotionAttribute';
import NewBuildBanner from './components/NewBuildBanner';
import styles from './styles/App.module.css';

// The Assets page is its own visit: loaded when opened (the perf overhaul), not with every table.
const AssetsPage = lazy(() => import('./components/assets/AssetsPage'));

function AppScreens() {
  const { pushToast } = useToast();
  // `?workshop` was the folder Workshop; it retired into the Assets page (assets stage 4), so an old bookmark lands there.
  const [assets, setAssets] = useUrlFlag('assets', 'workshop');
  const auth = useShellAuth(pushToast);
  const { user, session, displayName } = auth;
  // `?join=CODE` - the invite link. One URL is the whole handover.
  const [joinCodeInput, setJoinCodeInput] = useState(joinCodeFromUrl);
  const gmForm = useGmSignInForm();
  const entry = useShellSessionEntry({ ...auth, joinCodeInput, pushToast });

  if (auth.loading) return <div className={styles.loading}>Loading...</div>;
  if (!user) {
    return (
      <AuthScreen
        form={gmForm}
        authBusy={entry.authBusy}
        onPlay={entry.handlePlay}
        onGoogleSignIn={entry.handleGoogleSignIn}
        onPasswordSignIn={entry.handlePasswordSignIn}
      />
    );
  }
  if (assets) {
    return (
      <Suspense fallback={null}>
        <AssetsPage user={user} onExit={() => setAssets(false)} />
      </Suspense>
    );
  }
  if (!session.id) {
    return (
      <SessionScreen
        user={user}
        displayName={displayName}
        setDisplayName={auth.setDisplayName}
        pendingRejoin={auth.pendingRejoin}
        rejoining={entry.rejoining}
        onRejoin={entry.handleRejoin}
        onDismissRejoin={entry.handleDismissRejoin}
        sessionBusy={entry.sessionBusy}
        onCreate={entry.handleCreate}
        joinCodeInput={joinCodeInput}
        setJoinCodeInput={setJoinCodeInput}
        onJoin={entry.handleJoin}
        onAssets={() => setAssets(true)}
      />
    );
  }
  return (
    <DeathWatchSimulator
      key={session.id}
      user={{ ...user, sessionName: displayName.trim() || user.displayName }}
      session={session}
      setSession={auth.setSession}
      onLeave={entry.handleLeaveSession}
    />
  );
}

/**
 * The app: whichever screen it is on, with the root's motion switch worn on every one (MotionAttribute), and the
 * banner that says when a newer version has gone live (NewBuildBanner).
 */
function App() {
  return (
    <>
      <MotionAttribute />
      <NewBuildBanner />
      <AppScreens />
    </>
  );
}

export default App;
